import React, { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";

import MiscContext from "./MiscContext";

import { DEVICE_MOBILE, DEVICE_TABLET } from "utils/constants";
import { useForumThreads } from "utils/hooks";

// Decide default them depending on user preferences
const defaultTheme = (
  window.matchMedia &&
  window.matchMedia('(prefers-color-scheme: light)').matches
) ? "light" : "dark";

function MiscProvider({ children }) {
  const [device, setDevice] = useState("mobile");
  const [theme, setTheme] = useState(() => localStorage.getItem("theme") ?? defaultTheme);
  const [rating, setRating] = useState(() => localStorage.getItem("rating") ?? "all");
  const forumThreads = useForumThreads({ limit: 5, locale: "en" });
  const pinnedAdministrationThreads = useForumThreads({ limit: 2, group: "administration", sticky: true, daysAgo: 14 });

  // Set body theme class
  useEffect(() => {
    document.body.classList.add(theme);
    return () => document.body.classList.remove(theme);
  }, [theme]);

  // Handle window resize listener
  useEffect(() => {
    let timeout;

    function handleResize() {
      if (timeout) {
        clearTimeout(timeout);
      }

      timeout = setTimeout(() => {
        const width = window.innerWidth;
        let size = "desktop";

        if (width < DEVICE_MOBILE) {
          size = "mobile";
        } else if (width < DEVICE_TABLET) {
          size = "tablet";
        }

        if (device !== size) {
          setDevice(size);
        }
      }, 200);
    }

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => {
      clearTimeout(timeout);
      window.removeEventListener("resize", handleResize);
    }
  }, [device, setDevice]);

  const _setTheme = useCallback(newTheme => {
    localStorage.setItem("theme", newTheme);
    setTheme(newTheme);
  }, [setTheme]);

  const _setRating = useCallback(newRating => {
    localStorage.setItem("rating", newRating);
    setRating(newRating);
  }, [setRating]);

  return (
    <MiscContext.Provider value={{
      device,
      theme,
      rating,
      forumThreads,
      pinnedAdministrationThreads,
      setTheme: _setTheme,
      setRating: _setRating,
    }}>
      { children }
    </MiscContext.Provider>
  );
}

MiscProvider.propTypes = {
  children: PropTypes.any,
};

export default MiscProvider;
